import "./ImageSelector.scss";

import React, { useCallback, useState, useEffect } from "react";
import { useSWRConfig } from "swr";
import clsx from "clsx";

import Modal from "../../container/Modal/Modal";
import DragAndDropFile from "../DragAndDropFile/DragAndDropFile";
import LoadingSpinner from "../../common/LoadingSpinner/LoadingSpinner";
import { IconUpload } from "../../../assets";

import Endpoint from "../../../lib/api/Endpoint";
import { useMutation, useImmutable } from "../../../lib/hook/SwrHooks";

const VALID_FILE_TYPES = ["image/png", "image/jpeg"];

interface SignedImage {
	src: string;
	name: string;
}

interface ImageProps {
	src: string | SignedImage;
	onClick: (src: string | SignedImage) => void;
	selected: boolean;
}

const Image: React.FC<ImageProps> = ({ src, onClick, selected }) => {
	const handleSelect = useCallback(() => {
		onClick(src);
	}, [src, onClick]);

	return (
		<div
			className={"ImageSelector__modal-content-image-wrapper"}
			onClick={handleSelect}
		>
			<div
				className={clsx("ImageSelector__modal-content-image", {
					"--selected": selected,
				})}
			>
				<img src={typeof src === "string" ? src : src.src} alt={""} />
			</div>
		</div>
	);
};

interface ImageSelectorProps {
	value?: string | SignedImage;
	name?: string;
	defaultImages?: string[];
	onChange: (value: string | SignedImage) => void;
	customImageAPI: Endpoint;
	imageUploadAPI: Endpoint;
	imagePath?: string;
	disabled?: boolean;
}

const ImageSelector: React.FC<ImageSelectorProps> = (props) => {
	const {
		value,
		defaultImages,
		onChange,
		customImageAPI,
		imageUploadAPI,
		imagePath,
		disabled,
	} = props;
	const [modalActive, setModalActive] = useState(false);
	const [uploadError, setUploadError] = useState(false);
	const swrKey = `${customImageAPI?.id}-${imagePath}`;
	const {
		data: images,
		mutate: updateImages,
		...imageState
	} = useImmutable(
		swrKey,
		async () => {
			const res = await customImageAPI?.submit({ query: { path: imagePath } });
			return res?.data;
		},
		[],
	);
	const { trigger: uploadImage, isMutating: imageUploading } = useMutation(
		[imageUploadAPI?.path, imagePath],
		imageUploadAPI,
	);

	const toggleModal = useCallback(() => {
		if (!modalActive && disabled) {
			return;
		}

		setModalActive(!modalActive);
	}, [modalActive, disabled]);

	const handleSelect = useCallback(
		(src: string | SignedImage) => {
			onChange(src);
			setModalActive(false);
		},
		[onChange],
	);

	const handleImageUpload = useCallback(
		async (files: FileList) => {
			const file = files[0];

			if (!VALID_FILE_TYPES.includes(file.type)) {
				setUploadError(true);
				return;
			}

			if (uploadError) {
				setUploadError(false);
			}

			const response = await uploadImage({
				body: files[0],
				query: { path: imagePath },
				headers: {
					"Content-Type": files[0].type,
				},
			});

			updateImages(
				async (currentData: any) => {
					return [response, ...currentData];
				},
				{ revalidate: false },
			);
		},
		[uploadError, imagePath, updateImages, uploadImage],
	);

	return (
		<>
			<div className={clsx("ImageSelector", { "--disabled": disabled })}>
				<div
					className={"ImageSelector__current-image-wrapper"}
					onClick={toggleModal}
				>
					<img
						src={typeof value === "string" ? value : value?.src}
						className={"ImageSelector__current-image"}
						alt={""}
					/>

					<div className={"ImageSelector__action"}>
						<p>Replace</p>
					</div>
				</div>
			</div>

			<Modal
				visible={modalActive}
				onVisibleChange={setModalActive}
				className={"ImageSelector__modal"}
			>
				<div className={"ImageSelector__modal-content"}>
					<div className={"ImageSelector__modal-images"}>
						<h3>Default Images</h3>
						<div className={"ImageSelector__modal-content-default-images"}>
							{defaultImages?.map((image, index) => (
								<Image
									key={index}
									src={image}
									onClick={handleSelect}
									selected={image === value}
								/>
							))}
						</div>

						<h3>Uploaded Images</h3>
						<div
							className={clsx("ImageSelector__modal-content-default-images", {
								"--loading": imageState.isLoading,
							})}
						>
							{imageState.isLoading && <LoadingSpinner />}
							{imageUploading && (
								<div
									className={
										"ImageSelector__modal-content-image-wrapper --skeleton"
									}
								>
									<div className={"ImageSelector__modal-content-image"} />
								</div>
							)}
							{images?.map((image: any, index: number) => (
								<Image
									key={index}
									src={image}
									onClick={handleSelect}
									selected={image.src === value}
								/>
							))}
						</div>
					</div>
					{imageUploadAPI && (
						<div className={"ImageSelector__modal-actions"}>
							<DragAndDropFile
								accept={VALID_FILE_TYPES}
								placeholder={"Upload a new image"}
								icon={<IconUpload />}
								onAfterDrop={handleImageUpload}
								showError={uploadError}
								errorMessage={"Inavlid file type"}
							/>
						</div>
					)}
				</div>
			</Modal>
		</>
	);
};

export default ImageSelector;
